import { Link } from '@tanstack/react-router';

type Item = { type: 'page'; page: number } | { type: 'ellipsis' };

/** Page 1 is the bare category URL, so its link must not carry `?page=1`. */
const pageSearch = (page: number) => ({ page: page <= 1 ? undefined : page });

function buildItems(current: number, total: number, around = 1): Item[] {
  const pages = new Set<number>([1, total, current]);
  for (let i = 1; i <= around; i += 1) {
    if (current - i >= 1) pages.add(current - i);
    if (current + i <= total) pages.add(current + i);
  }
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const items: Item[] = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const cur = sorted[i]!;
    items.push({ type: 'page', page: cur });
    const next = sorted[i + 1];
    if (next !== undefined && next - cur > 1) items.push({ type: 'ellipsis' });
  }
  return items;
}

export function Pagination({
  slug,
  page,
  totalPages,
}: {
  slug: string;
  page: number;
  totalPages: number;
}) {
  if (totalPages <= 1) return null;
  const items = buildItems(page, totalPages, 1);
  const hasPrev = page > 1;
  const hasNext = page < totalPages;
  return (
    <div className="pagination-item">
      <nav aria-label="Page navigation">
        <ul className="pagination">
          {hasPrev && (
            <li className="page-item">
              <Link
                className="page-link"
                to="/category/$"
                params={{ _splat: slug }}
                search={pageSearch(page - 1)}
                aria-label="Previous"
              >
                <span aria-hidden="true"><i className="fas fa-caret-right" /></span>
              </Link>
            </li>
          )}
          {items.map((it, idx) =>
            it.type === 'ellipsis' ? (
              <li key={`e${idx}`} className="page-item disabled">
                {/* Gap marker, not a link. */}
                <span className="page-link" aria-hidden="true">
                  ...
                </span>
              </li>
            ) : (
              <li
                key={it.page}
                className={`page-item${it.page === page ? ' active' : ''}`}
              >
                <Link
                  className="page-link"
                  to="/category/$"
                  params={{ _splat: slug }}
                  search={pageSearch(it.page)}
                >
                  {it.page}
                </Link>
              </li>
            ),
          )}
          {hasNext && (
            <li className="page-item">
              <Link
                className="page-link"
                to="/category/$"
                params={{ _splat: slug }}
                search={pageSearch(page + 1)}
                aria-label="Next"
              >
                <span aria-hidden="true"><i className="fas fa-caret-left" /></span>
              </Link>
            </li>
          )}
        </ul>
      </nav>
    </div>
  );
}
